import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';

import { UserService } from './core/user';
import {
  buildLocationErrorUrl,
  buildLocationRedirectUrl,
  isLocationAvailable,
  pickLandingLocation,
  stripLocationPrefix
} from './shared/navbar/location-utils';

@Injectable({
  providedIn: 'root'
})
export class LocationContextGuard implements CanActivate {
  constructor(
    private readonly userService: UserService,
    private readonly router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const locationNumber = route.paramMap.get('locationNumber');

    return this.userService.getUser().pipe(
      map((userData) => {
        // User setup hasn't completed or failed - nothing safe to validate/redirect against.
        // Block navigation instead of crashing; app.component's userError$ banner covers
        // surfacing the failure to the user.
        if (!userData?.locations?.length) {
          return false;
        }

        const matchedLocation = userData.locations.find((loc) => loc.locationNumber === locationNumber);

        if (!matchedLocation) {
          const fallbackLocation = pickLandingLocation(
            userData.locations,
            userData.userSettings?.favoriteLocations,
            this.userService.getCurrentSelectedLocation?.()
          );
          if (!fallbackLocation?.locationNumber) {
            return false;
          }
          return this.router.parseUrl(buildLocationRedirectUrl(fallbackLocation, stripLocationPrefix(state.url)));
        }

        // Any route under /:locationNumber (discover, meet, care, calendar, ...) lands here, so
        // this is the single place that keeps an unavailable location off every feature page.
        if (!isLocationAvailable(matchedLocation)) {
          return this.router.parseUrl(buildLocationErrorUrl(matchedLocation.locationNumber));
        }

        this.userService.getSelectedLocation().subscribe((selectedLocation) => {
          if (selectedLocation?.locationNumber !== matchedLocation.locationNumber) {
            this.userService.updateSelectedLocation(matchedLocation);
          }
        });

        return true;
      })
    );
  }
}
